import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowRightOutlined, CaretRightOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import {
    solFadeRight,
    solScaleIn,
    solSlideUp,
    solStagger,
    solViewport,
} from "../components/solutions/shared/solutionMotion";
// import { ServiceVideoPortfolio } from "../components/solutions/shared/ServiceVideoPortfolio";
import { VeProductionPipelineSection } from "../components/solutions/video-editing/VeProductionPipelineSection";
// import {
//     VIDEO_PORTFOLIO_SUBTITLE,
//     videoEditingPortfolioItems,
// } from "../data/servicePortfolioGalleries";
import "../styles/solution-pages-base.css";
import "../styles/service-portfolio.css";
import "../styles/video-editing-solution.css";

const capabilities = [
    { name: "Reels Editing", tag: "Short-form" },
    { name: "YouTube Videos", tag: "Long-form" },
    { name: "Corporate Videos", tag: "Business" },
    { name: "Product Videos", tag: "E-commerce" },
    { name: "Motion Graphics", tag: "Animation" },
    { name: "Promotional Videos", tag: "Campaigns" },
];

export const VideoEditingPage = () => {
    const navigate = useNavigate();
    const [playing, setPlaying] = useState(false);

    return (
        <div className="sol-page ve-page">
            {/* Section 1 – Video Studio Hero */}
            <section className="ve-studio">
                <div className="sol-container ve-studio__grid">
                    <motion.div
                        className="ve-studio__copy"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solStagger}
                    >
                        <motion.div className="ve-hero-heading" variants={solFadeRight}>
                            <h1 className="ve-hero-title">Video Editing</h1>
                            <p className="ve-hero-subtitle">
                                Cinematic edits that <span className="ve-accent">captivate</span>
                            </p>
                        </motion.div>
                        <motion.p className="ve-lead" variants={solFadeRight} custom={0.05}>
                            Professional video production for social, corporate, and promotional content —
                            crafted with precision and storytelling at every frame.
                        </motion.p>
                    </motion.div>

                    <motion.div
                        className="ve-timeline-stage"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solScaleIn}
                    >
                        <div className={`ve-viewport${playing ? " is-playing" : ""}`}>
                            <button
                                type="button"
                                className="ve-play-btn"
                                onClick={() => setPlaying((p) => !p)}
                                aria-label={playing ? "Pause preview" : "Play preview"}
                            >
                                <CaretRightOutlined />
                            </button>
                            <div className="ve-layer ve-layer--1" />
                            <div className="ve-layer ve-layer--2" />
                            <div className="ve-layer ve-layer--3" />
                        </div>

                        <div className="ve-timeline">
                            <div className="ve-timeline__ruler">
                                {[...Array(12)].map((_, i) => (
                                    <span key={i} className="ve-timeline__tick" />
                                ))}
                            </div>
                            <div className="ve-timeline__tracks">
                                <div className="ve-track ve-track--video">
                                    <span>Video</span>
                                </div>
                                <div className="ve-track ve-track--audio">
                                    <span>Audio</span>
                                </div>
                                <div className="ve-track ve-track--fx">
                                    <span>FX</span>
                                </div>
                            </div>
                            <motion.div
                                className="ve-playhead"
                                animate={{ left: playing ? "85%" : "20%" }}
                                transition={{ duration: playing ? 8 : 0.5, ease: playing ? "linear" : [0.22, 1, 0.36, 1] }}
                            />
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Section 2 – Editing Capabilities */}
            <section className="ve-capabilities">
                <div className="sol-container">
                    <motion.div
                        className="ve-section-head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solSlideUp}
                    >
                        <h2 className="ve-h2">Editing Capabilities</h2>
                        <p className="ve-sub">Full-spectrum video production for every platform and purpose.</p>
                    </motion.div>

                    <motion.div
                        className="ve-cap-grid"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solStagger}
                    >
                        {capabilities.map((cap, i) => (
                            <motion.div
                                key={cap.name}
                                className={`ve-cap-item ve-cap-item--${i + 1}`}
                                variants={solScaleIn}
                                custom={i * 0.06}
                            >
                                <div className="ve-cap-item__film" />
                                <span className="ve-cap-item__tag">{cap.tag}</span>
                                <h3>{cap.name}</h3>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            <VeProductionPipelineSection />

            {/* Our Work section — temporarily hidden
            <ServiceVideoPortfolio
                items={videoEditingPortfolioItems}
                subtitle={VIDEO_PORTFOLIO_SUBTITLE}
                sectionId="ve-portfolio"
            />
            */}

            <section className="sol-cta">
                <div className="sol-container">
                    <motion.div
                        className="sol-cta__inner"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={solViewport}
                    >
                        <h2 className="sol-cta__title">Ready for professional video edits?</h2>
                        <p className="sol-cta__desc">
                            Share your footage — we&apos;ll deliver polished, platform-ready videos on schedule.
                        </p>
                        <Button className="sol-btn" onClick={() => navigate("/contact")}>
                            Start a Video Project <ArrowRightOutlined />
                        </Button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};
